import { useTranslation } from 'next-i18next';
import { useReducer, useState } from 'react';

import { NS } from '@/constants';
import { useAnimation } from '@/hooks';
import { useAppDispatch, useAppSelector } from '@/redux';
import { hideAlertPrimeurInfosModal } from '@/redux/slices/gui.slice';
import { primeurOnSubmit } from '@/redux/slices/primeur.slice';
import { ButtonType, CallToActionKind } from '@/types';

import CheckboxV2 from '@/components/common/CheckboxV2';
import Form from '@/components/common/form/Form';
import FormFieldV2 from '@/components/common/form/FormFieldV2';
import FullScreenContainerV2 from '@/components/layout/modals/FullScreenContainerV2';

import styles from './ModalAlertPrimeurInfos.module.scss';
import { useScreenSizeContext } from '@/components/ScreenSize.context';
import { ANIMATION } from '@/constants/animation';
import { ERROR_TYPE, FIELD_TYPE } from '@/constants/form';
import { STORE_ID } from '@/constants/stores';
import { URLS } from '@/constants/urls';
import { WARN_BY, WARN_BY_STATE } from '@/constants/primeur';

const NAMESPACES = [
  NS.COMMON
];

const FORM_ID = 'primeur-alert';

type FormData = {
  email: string;
  phoneNumber: string
}

export const ModalAlertPrimeurInfos: React.FC<{isOpen?:boolean}> = ({ isOpen = false }) => {
  const { t } = useTranslation(NAMESPACES);
  const dispatch = useAppDispatch();

  const { alertPrimeurProducts, alertPrimeurProductName } = useAppSelector(({ gui }) => gui);
  const { storeId } = useAppSelector(({ root }) => root.storeConf);

  const [warnByEmail, setWarnByEmail] = useState(true);
  const [warnBySms, setWarnBySms] = useState(false);
  const [selected, changeSelected] = useReducer((_, elem: string) => elem, WARN_BY[0]);

  const isSMSAvailable = ![STORE_ID.SINGAPORE, STORE_ID.USA].includes(storeId);
  const warnList = isSMSAvailable ? WARN_BY : [WARN_BY[0]];

  const isMobile = useScreenSizeContext().isMobile;
  const { animationState: animation, handleCloseModal } = useAnimation({});

  const updateWarnBy = () => (event: React.ChangeEvent<HTMLInputElement>) => {
    const target = event.target.value;
    changeSelected(target);
    if (target === WARN_BY_STATE.EMAIL) {
      setWarnByEmail(true);
      setWarnBySms(false);
    } else if (target === WARN_BY_STATE.SMS) {
      setWarnByEmail(false);
      setWarnBySms(true);
    } else if (target === WARN_BY_STATE.EMAILANDSMS) {
      setWarnByEmail(true);
      setWarnBySms(true);
    }
  };

  const onSubmit = (data: FormData) => {
    dispatch(primeurOnSubmit({
      t,
      content: {
        email: data.email,
        tel: data.phoneNumber,
        storeId,
        alerts: alertPrimeurProducts,
        warnByEmail,
        warnBySms,
        toCreate: true
      }
    }));
  };

  const closeModal = () => {
    handleCloseModal();
    setTimeout(() => {
      dispatch(hideAlertPrimeurInfosModal());
    }, ANIMATION.DELAY_MODAL);
  };

  const headlineProps = {
    buttonRight: {
      icon: {
        url: URLS.ORION_CLOSE_BLACK,
        alt: 'close',
        height: 12,
        width: 12
      },
      onClick: closeModal,
      kind: CallToActionKind.TERTIARY
    },
    title: t('alert.manageMyAlerts'),
    withPadding: true
  };

  const footerProps = [
    {
      id: 'footer-submit',
      type: 'submit' as ButtonType,
      label: t('btn.save'),
      form: FORM_ID,
      className: styles.footer
    },
    {
      label: t('btn.cancel'),
      kind: CallToActionKind.SECONDARY,
      onClick: closeModal
    }
  ];

  return (
    <FullScreenContainerV2
      onClose={closeModal}
      overlay
      classNames={{ container: styles.container }}
      animationClass={animation}
      headline={headlineProps}
      footer={footerProps}
      isOpen={isOpen}
      padding={isMobile ? 'none' : 'xl'}
    >
      {alertPrimeurProductName && (
        <p dangerouslySetInnerHTML={{ __html: t('alert.productIntro', { name: alertPrimeurProductName }) }} />
      )}
      <Form id={FORM_ID} onSubmit={onSubmit}>
        <div className={styles.content}>
          <FormFieldV2
            id="ti-primeur-alert-email"
            name="email"
            type={FIELD_TYPE.TEXT}
            label={t('forms.email')}
            validators={[ERROR_TYPE.REQUIRED, ERROR_TYPE.EMAIL]}
            required
          />
          <p className={styles.required}>
            {t('alert.emailIsRequired')}
          </p>
          {isSMSAvailable && (
            <FormFieldV2
              id="ti-primeur-alert-phone"
              name="phoneNumber"
              labelIndicator={t('forms.phoneIndicator')}
              type={FIELD_TYPE.PHONE}
              label={t('forms.phone')}
              validators={warnBySms ? [ERROR_TYPE.REQUIRED, ERROR_TYPE.PHONE, ERROR_TYPE.ONLY_NUMBERS] : ['']}
              required={warnBySms}
            />
          )}

          <div className={styles.options}>
            <p>{t('alert.beAlertedBy')} :</p>
            {warnList.map((item, index) => (
              <CheckboxV2
                className={styles.checkbox} value={item}
                key={item + String(index)} checked={item === selected}
                onChange={updateWarnBy()} label={t(`alert.${item}`)}
              />
            ))}
          </div>
        </div>
      </Form>
    </FullScreenContainerV2>
  );
};

export default ModalAlertPrimeurInfos;
